/* eslint-disable complexity */
import { Direction } from '@angular/cdk/bidi';
import { Directive, EventEmitter, Injectable, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { USER_LOCAL_STORAGE_KEY } from 'app.config';
import { Observable, concat, defer, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthenticationProvider } from '../enums/authentication-provider.enum';
import { ISiteSettings } from '../interfaces/site-settings.interfaces';

@Directive()
@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  @Output()
  public languageUpdated = new EventEmitter<string>();

  public isRtlLanguage$: Observable<boolean>;
  /**
   * The sites current language
   */
  private _currentLanguage: string | undefined;

  /**
   * List of current accepted RTL languages
   */
  // TODO fill out with more RTL languages
  private rtlLanguages = ['ar-ae'];

  /**
   * Gets the current language
   */
  public get currentLanguage(): string {
    return this._currentLanguage;
  }

  public currentDirection: Direction = 'ltr';

  constructor(private translateService: TranslateService) {
    this.isRtlLanguage$ = concat(
      defer(() => of(this.translateService.currentLang)),
      this.translateService.onLangChange.pipe(map((event) => event.lang))
    ).pipe(map((lang) => this.rtlLanguages.includes(lang)));
  }

  /**
   * Initialises the language service by setting the default language and using either
   * a previously saved language or the default
   */
  public initLanguageService(initialLanguage: string) {
    /* sets default language to english so if there are any missing translations
    they will be visible in english and not just an empty space*/
    this.translateService.setDefaultLang('en-gb');
    try {
      const savedLang = localStorage.getItem('ten-language-settings');
      if (!savedLang || savedLang === undefined || (savedLang && savedLang !== initialLanguage)) {
        this.setLanguage(initialLanguage);
      } else {
        this.setLanguage(savedLang);
      }
    } catch (err) {
      console.error(err);
    }
  }

  /**
   * Sets the current language and saves it in local storage
   * @param lang
   */
  public setLanguage(lang = 'en-gb') {
    this.translateService.use(lang);
    this._currentLanguage = lang;
    this.setDirAttribute();
    this.setLangAttribute();
    this.languageUpdated.emit(lang);

    try {
      localStorage.setItem('ten-language-settings', lang);
    } catch (err) {
      console.error('Could not set local storage for language', err);
    }
  }

  /**
   * Sets the direction attribute on the HTML element for ltr and rtl languages
   */
  public setDirAttribute() {
    const direction = this.isRtlLanguage() ? 'rtl' : 'ltr';
    this.currentDirection = direction as Direction;
    document.documentElement.setAttribute('dir', direction);
  }

  /**
   * Sets the language attribute on the HTML element
   */
  public setLangAttribute() {
    document.documentElement.setAttribute('lang', this._currentLanguage);
  }

  /**
   * Checks if the current language is a RTL language
   */
  public isRtlLanguage(): boolean {
    return this.rtlLanguages.includes(this._currentLanguage);
  }

  public preparingSiteLanguageAttribute(siteSettings: ISiteSettings): string {
    if (!siteSettings.language) {
      console.warn('No language detected falling back to english');
      return 'en-gb';
    }

    if (
      siteSettings.authentication_provider.provider ===
        (AuthenticationProvider.visa_lac ||
          AuthenticationProvider.creditsaison ||
          AuthenticationProvider.creditsaisondiamond) &&
      !localStorage.getItem(USER_LOCAL_STORAGE_KEY)
    ) {
      const possibleLanguages = ['es', 'en', 'pt_BR', 'ja-jp'];
      const browserLanguage = window.navigator.language.toLocaleLowerCase().slice(0, 2);
      const matchingLanguage = possibleLanguages.find((possibleLanguage) =>
        possibleLanguage.startsWith(browserLanguage)
      );

      return matchingLanguage || 'en';
    }

    return siteSettings.language;
  }

  public mapLangcodeToLanguageName(langcode) {
    const rootLanguage = langcode.slice(0, 2);
    switch (rootLanguage) {
      case 'ar': // Arabic
        return 'العربية';
      case 'bg': // Bulgarian
        return 'български';
      case 'cs': // Czech
        return 'Čeština';
      case 'da': // Danish
        return 'Dansk';
      case 'de': // German
        return 'Deutsche';
      case 'es': // Spanish
        return 'Español';
      case 'fi': // Finnish
        return 'Suomi';
      case 'fr': // French
        return 'Français';
      case 'it': // Italian
        return 'Italiano';
      case 'ja': // Japanese
        return '日本語';
      case 'ko': // Korean
        return '한국어';
      case 'lt': // Lithuanian
        return 'Lietuvių';
      case 'lv': // Latvian
        return 'Latviešu';
      case 'nb': // Norwegian
        return 'Norsk';
      case 'nl': // Dutch
        return 'Nederlands';
      case 'pl': // Polish
        return 'Polski';
      case 'pt': // Portuguese
        return 'Portugués';
      case 'ru': // Russian
        return 'русский';
      case 'sk': // Slovak
        return 'Slovenčina';
      case 'sl': // Slovenian
        return 'Slovenščina';
      case 'sv': // Swedish
        return 'Svenska';
      case 'zh': // Chinese
        return '中文';
      default:
        return 'English';
    }
  }
}
